import { Trans } from '@lingui/react'
import { useAtom } from 'jotai'

import { useSubmitEmploymentTypeMutation } from '@twisto/api-graphql'
import { FormSubmitButton } from '@twisto/components/atoms/button'
import type { CastedSubmitHandler } from '@twisto/components/atoms/form'
import { useForm } from '@twisto/components/atoms/form'
import { Headline, HeadlineTitle } from '@twisto/components/atoms/headline'
import { Input } from '@twisto/components/atoms/input'
import { AutoComplete } from '@twisto/components/molecules/autocomplete'
import {
  FlowForm,
  submittingFlowFormAtom,
  useSubmittingWrapper,
} from '@twisto/flow-views/components'
import { useFlow } from '@twisto/flow-views/flow/flow.context'
import { validator } from '@twisto/form'

import * as styles from './employment-type-step.styles'

const fieldName = {
  value: 'value',
} as const

type FormValues = {
  [fieldName.value]: string
}

export const EmploymentTypeStep = () => {
  const [submitting] = useAtom(submittingFlowFormAtom)
  const { flow, sessionId } = useFlow<'EmploymentTypeStep'>()
  const [submitIEmploymentType] = useSubmitEmploymentTypeMutation()
  const submittingWrapper = useSubmittingWrapper()

  const { employmentType } = flow.currentStep

  const formContext = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      value: employmentType.value ?? '',
    },
  })

  const submitHandler: CastedSubmitHandler<FormValues> = ({ value }) =>
    submittingWrapper(
      submitIEmploymentType({
        variables: {
          auth: {
            sessionId,
          },
          input: {
            employmentType: value,
          },
        },
      })
    )

  const mappedOptions = employmentType.options?.map((option) => ({
    id: option.value,
    value: option.label,
    primary: option.label,
  }))

  return (
    <>
      <Headline align="start" css={styles.headlineSection}>
        <HeadlineTitle>
          <Trans id="flow.employmentType.title" />
        </HeadlineTitle>
      </Headline>
      <FlowForm {...formContext} onSubmit={submitHandler}>
        <AutoComplete
          name={fieldName.value}
          options={mappedOptions ?? []}
          renderInput={(inputProps) => <Input autoFocus {...inputProps} />}
          rules={{
            required:
              employmentType.required &&
              validator.getValidationMessages().required,
          }}
        />
        <FormSubmitButton css={styles.submitButton} disabled={submitting}>
          <Trans id="flow.selectFieldList.formSubmit" />
        </FormSubmitButton>
      </FlowForm>
    </>
  )
}
